import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useHistory } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { toast } from "react-toastify";
import clsx from "clsx";

import AppPageTitle from "../../components/AppPageTitle";
import PermissionModal from "../../components/PermissionModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useBusinessTimezone } from "../../context/BusinessTimezone/BusinessTimezoneContext";
import {
	formatMessageCalendarDay,
	formatMessageClock,
} from "../../helpers/dateTimeFormat";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const FOCUS_KEYS = [
	"sellers",
	"talk_time",
	"quality",
	"queue_volume",
	"complaints",
	"people_improve",
	"lead_interest",
	"lead_classification",
	"lead_helped",
];
const MESSAGE_FOCUS = "messages";
const AUTO_MESSAGE_FOCUSES = [
	"complaints",
	"people_improve",
	"lead_interest",
	"lead_classification",
	"lead_helped",
];
const SCOPE_OPTIONS = [
	{ value: "team", labelKey: "adminAiReports.scope.team" },
	{ value: "ticket", labelKey: "adminAiReports.scope.ticket" },
	{ value: "contact", labelKey: "adminAiReports.scope.contact" },
];

const PERIOD_TYPES = [
	{ value: "day", labelKey: "adminAiReports.period.day" },
	{ value: "hour", labelKey: "adminAiReports.period.hour" },
	{ value: "month", labelKey: "adminAiReports.period.month" },
];

function toLocalInputValue(date, withTime) {
	const pad = (n) => String(n).padStart(2, "0");
	const y = date.getFullYear();
	const m = pad(date.getMonth() + 1);
	const d = pad(date.getDate());
	if (!withTime) return `${y}-${m}-${d}`;
	return `${y}-${m}-${d}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultDateFrom(periodType) {
	const now = new Date();
	if (periodType === "month") {
		return toLocalInputValue(new Date(now.getFullYear(), now.getMonth(), 1), false);
	}
	if (periodType === "hour") {
		const from = new Date(now.getTime() - 60 * 60 * 1000);
		return toLocalInputValue(from, true);
	}
	return toLocalInputValue(now, false);
}

function defaultDateTo(periodType) {
	const now = new Date();
	if (periodType === "hour") {
		return toLocalInputValue(now, true);
	}
	return toLocalInputValue(now, false);
}

function IconChevronDown() {
	return (
		<svg
			className="size-4 shrink-0 text-default-500 dark:text-default-400"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
		>
			<path d="m6 9 6 6 6-6" />
		</svg>
	);
}

function IconSpark() {
	return (
		<svg
			className="size-4 shrink-0"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
		>
			<path d="M12 3v3" />
			<path d="M12 18v3" />
			<path d="M3 12h3" />
			<path d="M18 12h3" />
			<path d="m5.6 5.6 2.1 2.1" />
			<path d="m16.3 16.3 2.1 2.1" />
			<path d="m16.3 5.6-2.1 2.1" />
			<path d="m5.6 16.3 2.1-2.1" />
			<circle cx="12" cy="12" r="3.5" />
		</svg>
	);
}

function IconRefresh() {
	return (
		<svg
			className="size-4 shrink-0"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
		>
			<path d="M21 2v6h-6" />
			<path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
			<path d="M3 22v-6h6" />
			<path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
		</svg>
	);
}

function FilterField({ label, hint, children }) {
	return (
		<div className="space-y-2">
			<label className="block text-sm font-medium text-default-700 dark:text-default-200">
				{label}
			</label>
			{children}
			{hint ? (
				<p className="m-0 text-xs text-default-500 dark:text-default-400">{hint}</p>
			) : null}
		</div>
	);
}

function MultiFilterSelect({
	label,
	options,
	selectedIds,
	onChange,
	placeholder,
	emptyText,
}) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);

	useEffect(() => {
		if (!open) return undefined;
		const handleOutsideClick = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [open]);

	const selectedOptions = options.filter((option) =>
		selectedIds.some((id) => String(id) === String(option.id))
	);

	const summary =
		selectedOptions.length === 0
			? placeholder
			: selectedOptions.length <= 2
				? selectedOptions.map((option) => option.name).join(", ")
				: i18n.t("adminAiReports.selectedCount", {
						count: selectedOptions.length,
				  });

	const handleToggle = (optionId) => {
		const id = Number(optionId);
		const exists = selectedIds.some((v) => String(v) === String(optionId));
		onChange(
			exists
				? selectedIds.filter((v) => String(v) !== String(optionId))
				: [...selectedIds, id]
		);
	};

	return (
		<div ref={containerRef} className="relative">
			<FilterField label={label}>
				<button
					type="button"
					className="form-input flex w-full items-center justify-between gap-3 text-left"
					onClick={() => setOpen((current) => !current)}
				>
					<span
						className={
							selectedOptions.length > 0
								? "truncate text-default-900 dark:text-default-100"
								: "truncate text-default-500 dark:text-default-400"
						}
					>
						{summary}
					</span>
					<IconChevronDown />
				</button>
			</FilterField>

			{selectedOptions.length > 0 ? (
				<div className="mt-2 flex flex-wrap gap-2">
					{selectedOptions.map((option) => (
						<button
							key={option.id}
							type="button"
							className="inline-flex items-center gap-2 rounded-full border border-default-200 bg-default-100 px-3 py-1 text-xs font-medium text-default-700 transition hover:border-default-300 hover:bg-default-150 dark:border-default-700 dark:bg-default-800 dark:text-default-200"
							onClick={() => handleToggle(option.id)}
						>
							<span>{option.name}</span>
							<span className="text-[11px] text-default-500">×</span>
						</button>
					))}
				</div>
			) : null}

			{open ? (
				<div className="absolute z-20 mt-2 max-h-56 w-full overflow-y-auto rounded-xl border border-default-200 bg-white p-2 shadow-lg dark:border-default-700 dark:bg-default-900">
					{!options.length ? (
						<p className="m-0 px-2 py-3 text-sm text-default-500">{emptyText}</p>
					) : (
						options.map((option) => {
							const checked = selectedIds.some(
								(id) => String(id) === String(option.id)
							);
							return (
								<label
									key={option.id}
									className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-default-100 dark:hover:bg-default-800"
								>
									<input
										type="checkbox"
										checked={checked}
										onChange={() => handleToggle(option.id)}
									/>
									<span className="truncate">{option.name || `#${option.id}`}</span>
								</label>
							);
						})
					)}
				</div>
			) : null}
		</div>
	);
}

function SectionBlock({ title, children, className }) {
	return (
		<section
			className={clsx(
				"rounded-xl border border-default-200 bg-default-50/70 p-4 dark:border-default-700 dark:bg-default-950/40",
				className
			)}
		>
			<h4 className="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-default-500">
				{title}
			</h4>
			{children}
		</section>
	);
}

function temperatureClass(temp) {
	switch (String(temp || "").toLowerCase()) {
		case "quente":
			return "bg-danger/10 text-danger";
		case "morno":
			return "bg-warning/10 text-warning";
		case "frio":
			return "bg-info/10 text-info";
		default:
			return "bg-default-150 text-default-600 dark:bg-default-800 dark:text-default-300";
	}
}

function ReportDetail({ report, timeZone }) {
	const whenDay = formatMessageCalendarDay(report.createdAt, timeZone);
	const whenClock = formatMessageClock(report.createdAt, timeZone);
	const insights = Array.isArray(report.insights) ? report.insights : [];
	const ranking = Array.isArray(report.ranking) ? report.ranking : [];
	const recommendations = Array.isArray(report.recommendations)
		? report.recommendations
		: [];
	const focuses = Array.isArray(report.focuses) ? report.focuses : [];
	const filters = report.filters || {};
	const lead = report.leadAnalysis || null;
	const objections = Array.isArray(lead?.objections) ? lead.objections : [];
	const contactTags = Array.isArray(lead?.contactTags) ? lead.contactTags : [];

	return (
		<article className="card">
			<div className="card-body space-y-4">
				<header className="flex flex-wrap items-start justify-between gap-3 border-b border-default-200 pb-4 dark:border-default-700">
					<div className="min-w-0">
						<h3 className="m-0 text-lg font-semibold text-default-900 dark:text-default-50">
							{report.title}
						</h3>
						<p className="m-0 mt-1 text-sm text-default-500">
							{whenDay} · {whenClock}
							{report.createdByName
								? ` · ${i18n.t("adminAiReports.byAuthor", {
										name: report.createdByName,
								  })}`
								: ""}
						</p>
					</div>
					<div className="flex flex-wrap gap-1.5">
						{filters.scope ? (
							<span className="rounded-full bg-default-150 px-2.5 py-1 text-[11px] font-semibold text-default-700 dark:bg-default-800 dark:text-default-200">
								{i18n.t(`adminAiReports.scope.${filters.scope}`, {
									defaultValue: filters.scope,
								})}
							</span>
						) : null}
						{filters.periodType ? (
							<span className="rounded-full bg-default-150 px-2.5 py-1 text-[11px] font-semibold text-default-700 dark:bg-default-800 dark:text-default-200">
								{i18n.t(`adminAiReports.period.${filters.periodType}`, {
									defaultValue: filters.periodType,
								})}
							</span>
						) : null}
						{focuses.map((f) => (
							<span
								key={f}
								className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary"
							>
								{i18n.t(`adminAiReports.focuses.${f}`, { defaultValue: f })}
							</span>
						))}
					</div>
				</header>

				{report.question ? (
					<div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
						<p className="m-0 text-xs font-semibold uppercase tracking-wide text-primary">
							{i18n.t("adminAiReports.fields.question")}
						</p>
						<p className="m-0 mt-1 text-sm text-default-800 dark:text-default-100">
							{report.question}
						</p>
					</div>
				) : null}

				{lead ? (
					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
						<div className="rounded-xl border border-default-200 bg-default-50/80 px-4 py-3 dark:border-default-700 dark:bg-default-950/40">
							<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-500">
								{i18n.t("adminAiReports.lead.temperature")}
							</p>
							<span
								className={clsx(
									"mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-bold capitalize",
									temperatureClass(lead.leadTemperature)
								)}
							>
								{i18n.t(
									`adminAiReports.lead.tempValues.${lead.leadTemperature}`,
									{ defaultValue: lead.leadTemperature || "—" }
								)}
							</span>
						</div>
						<div className="rounded-xl border border-default-200 bg-default-50/80 px-4 py-3 dark:border-default-700 dark:bg-default-950/40">
							<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-500">
								{i18n.t("adminAiReports.lead.score")}
							</p>
							<p className="m-0 mt-2 text-2xl font-bold text-default-900 dark:text-default-50">
								{Number.isFinite(lead.leadScore) ? lead.leadScore : "—"}
								<span className="ms-1 text-sm font-medium text-default-400">
									/100
								</span>
							</p>
						</div>
						<div className="rounded-xl border border-default-200 bg-default-50/80 px-4 py-3 dark:border-default-700 dark:bg-default-950/40">
							<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-500">
								{i18n.t("adminAiReports.lead.helped")}
							</p>
							<p className="m-0 mt-2 text-sm font-semibold capitalize text-default-800 dark:text-default-100">
								{i18n.t(`adminAiReports.lead.helpedValues.${lead.helped}`, {
									defaultValue: lead.helped || "—",
								})}
							</p>
						</div>
						<div className="rounded-xl border border-default-200 bg-default-50/80 px-4 py-3 dark:border-default-700 dark:bg-default-950/40">
							<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-500">
								{i18n.t("adminAiReports.lead.classification")}
							</p>
							<p className="m-0 mt-2 text-sm font-semibold capitalize text-default-800 dark:text-default-100">
								{i18n.t(
									`adminAiReports.lead.classificationValues.${lead.classificationQuality}`,
									{ defaultValue: lead.classificationQuality || "—" }
								)}
							</p>
						</div>
					</div>
				) : null}

				{lead ? (
					<div className="grid gap-3 lg:grid-cols-2">
						<SectionBlock title={i18n.t("adminAiReports.lead.nextStep")}>
							<p className="m-0 text-sm leading-6 text-default-800 dark:text-default-100">
								{lead.nextStep || "—"}
							</p>
						</SectionBlock>
						<SectionBlock title={i18n.t("adminAiReports.lead.objections")}>
							{objections.length ? (
								<ul className="m-0 list-disc space-y-1.5 ps-5 text-sm text-default-800 dark:text-default-100">
									{objections.map((item, idx) => (
										<li key={`obj-${report.id}-${idx}`}>{item}</li>
									))}
								</ul>
							) : (
								<p className="m-0 text-sm text-default-500">
									{i18n.t("adminAiReports.lead.noObjections")}
								</p>
							)}
						</SectionBlock>
						<SectionBlock title={i18n.t("adminAiReports.lead.funnel")}>
							<p className="m-0 text-sm text-default-800 dark:text-default-100">
								{lead.funnelStage
									? lead.funnelStage
									: i18n.t("adminAiReports.lead.noFunnel")}
							</p>
							{lead.funnelAlignment ? (
								<p className="m-0 mt-2 text-sm text-default-600 dark:text-default-300">
									{lead.funnelAlignment}
								</p>
							) : null}
						</SectionBlock>
						<SectionBlock title={i18n.t("adminAiReports.lead.tags")}>
							{contactTags.length ? (
								<div className="flex flex-wrap gap-1.5">
									{contactTags.map((tag) => (
										<span
											key={tag}
											className="rounded-full bg-default-150 px-2.5 py-1 text-[11px] font-semibold text-default-700 dark:bg-default-800 dark:text-default-200"
										>
											{tag}
										</span>
									))}
								</div>
							) : (
								<p className="m-0 text-sm text-default-500">
									{i18n.t("adminAiReports.lead.noTags")}
								</p>
							)}
						</SectionBlock>
					</div>
				) : null}

				<div className="grid gap-3 lg:grid-cols-2">
					<SectionBlock
						title={i18n.t("adminAiReports.result.summary")}
						className="lg:col-span-2"
					>
						<p className="m-0 whitespace-pre-wrap text-sm leading-6 text-default-800 dark:text-default-100">
							{report.summary}
						</p>
					</SectionBlock>

					<SectionBlock title={i18n.t("adminAiReports.result.insights")}>
						{insights.length ? (
							<ul className="m-0 list-disc space-y-1.5 ps-5 text-sm text-default-800 dark:text-default-100">
								{insights.map((item, idx) => (
									<li key={`ins-${report.id}-${idx}`}>{item}</li>
								))}
							</ul>
						) : (
							<p className="m-0 text-sm text-default-500">—</p>
						)}
					</SectionBlock>

					<SectionBlock title={i18n.t("adminAiReports.result.recommendations")}>
						{recommendations.length ? (
							<ul className="m-0 list-disc space-y-1.5 ps-5 text-sm text-default-800 dark:text-default-100">
								{recommendations.map((item, idx) => (
									<li key={`rec-${report.id}-${idx}`}>{item}</li>
								))}
							</ul>
						) : (
							<p className="m-0 text-sm text-default-500">—</p>
						)}
					</SectionBlock>

					<SectionBlock
						title={i18n.t("adminAiReports.result.ranking")}
						className="lg:col-span-2"
					>
						{ranking.length ? (
							<div className="grid gap-2 sm:grid-cols-2">
								{ranking.map((item, idx) => (
									<div
										key={`rank-${report.id}-${idx}`}
										className="rounded-lg border border-default-200 bg-white px-3 py-3 dark:border-default-700 dark:bg-default-900"
									>
										<div className="flex items-start gap-3">
											<span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
												{idx + 1}
											</span>
											<div className="min-w-0">
												<p className="m-0 font-semibold text-default-900 dark:text-default-50">
													{item.name}
												</p>
												{item.metric ? (
													<p className="m-0 mt-0.5 text-xs font-medium text-primary">
														{item.metric}
													</p>
												) : null}
												{item.comment ? (
													<p className="m-0 mt-1 text-sm text-default-600 dark:text-default-300">
														{item.comment}
													</p>
												) : null}
											</div>
										</div>
									</div>
								))}
							</div>
						) : (
							<p className="m-0 text-sm text-default-500">—</p>
						)}
					</SectionBlock>
				</div>
			</div>
		</article>
	);
}

export default function AdminAiReports() {
	const { user } = useContext(AuthContext);
	const history = useHistory();
	const { timeZone } = useBusinessTimezone();

	const isAdmin = user?.profile === "admin";
	const [permissionOpen, setPermissionOpen] = useState(false);

	const [periodType, setPeriodType] = useState("day");
	const [dateFrom, setDateFrom] = useState(() => defaultDateFrom("day"));
	const [dateTo, setDateTo] = useState(() => defaultDateTo("day"));
	const [queueIds, setQueueIds] = useState([]);
	const [userIds, setUserIds] = useState([]);
	const [focuses, setFocuses] = useState([...FOCUS_KEYS]);
	const [question, setQuestion] = useState("");
	const [scope, setScope] = useState("team");
	const [ticketIdInput, setTicketIdInput] = useState("");
	const [contactQuery, setContactQuery] = useState("");
	const [contactResults, setContactResults] = useState([]);
	const [selectedContact, setSelectedContact] = useState(null);
	const [searchingContacts, setSearchingContacts] = useState(false);

	const [queues, setQueues] = useState([]);
	const [users, setUsers] = useState([]);
	const [reports, setReports] = useState([]);
	const [loadingList, setLoadingList] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [selectedId, setSelectedId] = useState(null);

	useEffect(() => {
		if (user && !isAdmin) {
			setPermissionOpen(true);
		}
	}, [user, isAdmin]);

	useEffect(() => {
		setDateFrom(defaultDateFrom(periodType));
		setDateTo(defaultDateTo(periodType));
	}, [periodType]);

	useEffect(() => {
		if (scope !== "contact") return undefined;
		if (!contactQuery || contactQuery.trim().length < 2) {
			setContactResults([]);
			return undefined;
		}
		let cancelled = false;
		const timer = setTimeout(async () => {
			setSearchingContacts(true);
			try {
				const { data } = await api.get("/contacts/", {
					params: { searchParam: contactQuery.trim(), pageNumber: 1 },
				});
				if (!cancelled) {
					setContactResults(Array.isArray(data?.contacts) ? data.contacts : []);
				}
			} catch (err) {
				if (!cancelled) toastError(err);
			} finally {
				if (!cancelled) setSearchingContacts(false);
			}
		}, 400);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [contactQuery, scope]);

	const loadOptions = useCallback(async () => {
		try {
			const [{ data: usersData }, { data: queuesData }] = await Promise.all([
				api.get("/users/"),
				api.get("/queue/"),
			]);
			setUsers(Array.isArray(usersData?.users) ? usersData.users : []);
			setQueues(Array.isArray(queuesData) ? queuesData : []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	const loadReports = useCallback(async () => {
		setLoadingList(true);
		try {
			const { data } = await apiV2.get("/admin/ai-reports");
			const list = Array.isArray(data?.reports) ? data.reports : [];
			setReports(list);
			setSelectedId((current) => {
				if (current && list.some((r) => r.id === current)) return current;
				return list.length ? list[0].id : null;
			});
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingList(false);
		}
	}, []);

	useEffect(() => {
		if (!isAdmin) return;
		loadOptions();
		loadReports();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAdmin]);

	const toggleFocus = (key) => {
		setFocuses((prev) => {
			const next = prev.includes(key)
				? prev.filter((f) => f !== key)
				: [...prev, key];
			return next;
		});
	};

	const readsMessages =
		scope !== "team" ||
		focuses.includes(MESSAGE_FOCUS) ||
		AUTO_MESSAGE_FOCUSES.some((k) => focuses.includes(k));

	const handleClearFilters = () => {
		setPeriodType("day");
		setDateFrom(defaultDateFrom("day"));
		setDateTo(defaultDateTo("day"));
		setQueueIds([]);
		setUserIds([]);
		setFocuses([...FOCUS_KEYS]);
		setQuestion("");
		setScope("team");
		setTicketIdInput("");
		setContactQuery("");
		setContactResults([]);
		setSelectedContact(null);
	};

	const handleGenerate = async () => {
		if (generating) return;
		if (!focuses.length) {
			toast.error(i18n.t("adminAiReports.errors.needFocus"));
			return;
		}
		if (scope === "ticket") {
			const id = Number(ticketIdInput);
			if (!id) {
				toast.error(i18n.t("adminAiReports.errors.needTicket"));
				return;
			}
		}
		if (scope === "contact" && !selectedContact?.id) {
			toast.error(i18n.t("adminAiReports.errors.needContact"));
			return;
		}
		if (
			scope === "team" &&
			(periodType === "hour" || periodType === "custom") &&
			(!dateFrom || !dateTo)
		) {
			toast.error(i18n.t("adminAiReports.errors.needDates"));
			return;
		}

		setGenerating(true);
		try {
			const payload = {
				scope,
				focuses,
				question: question.trim(),
			};
			if (scope === "team") {
				payload.periodType = periodType;
				payload.dateFrom = dateFrom;
				payload.dateTo = periodType === "hour" ? dateTo : dateFrom;
				payload.queueIds = queueIds;
				payload.userIds = userIds;
			}
			if (scope === "ticket") {
				payload.ticketId = Number(ticketIdInput);
			}
			if (scope === "contact") {
				payload.contactId = Number(selectedContact.id);
			}
			const { data } = await apiV2.post("/admin/ai-reports", payload);
			toast.success(i18n.t("adminAiReports.generatedSuccess"));
			setReports((prev) => [data, ...prev.filter((r) => r.id !== data.id)]);
			setSelectedId(data.id);
		} catch (err) {
			toastError(err);
		} finally {
			setGenerating(false);
		}
	};

	if (!isAdmin) {
		return (
			<PermissionModal
				open={permissionOpen}
				onClose={() => {
					setPermissionOpen(false);
					history.push("/");
				}}
			/>
		);
	}

	const needsRange = periodType === "hour";
	const selectedReport = reports.find((r) => r.id === selectedId) || null;

	return (
		<div className="app-admin-page">
			<AppPageTitle
				title={i18n.t("adminAiReports.title")}
				subtitle={i18n.t("adminAiReports.subtitle")}
			/>

			<div className="app-admin-page__scroll">
				<div className="space-y-5">
					<div className="card">
						<div className="card-body space-y-5">
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div>
									<h2 className="m-0 text-base font-semibold text-default-900 dark:text-default-50">
										{i18n.t("adminAiReports.formTitle")}
									</h2>
									<p className="m-0 mt-1 text-sm text-default-500">
										{i18n.t("adminAiReports.formHint")}
									</p>
								</div>
							</div>

							<div>
								<p className="mb-2 text-sm font-medium text-default-700 dark:text-default-200">
									{i18n.t("adminAiReports.fields.scope")}
								</p>
								<div className="flex flex-wrap gap-2">
									{SCOPE_OPTIONS.map((opt) => {
										const active = scope === opt.value;
										return (
											<button
												key={opt.value}
												type="button"
												onClick={() => setScope(opt.value)}
												className={clsx(
													"rounded-full px-3.5 py-1.5 text-xs font-semibold transition",
													active
														? "bg-primary text-white"
														: "border border-default-200 text-default-600 hover:border-primary/20 hover:bg-primary/10 hover:text-primary dark:border-default-700 dark:text-default-300"
												)}
											>
												{i18n.t(opt.labelKey)}
											</button>
										);
									})}
								</div>
								<p className="m-0 mt-2 text-xs text-default-500">
									{i18n.t(`adminAiReports.scopeHint.${scope}`)}
								</p>
							</div>

							{scope === "ticket" ? (
								<FilterField label={i18n.t("adminAiReports.fields.ticketId")}>
									<input
										type="number"
										min="1"
										className="form-input w-full max-w-xs"
										placeholder={i18n.t("adminAiReports.ticketPlaceholder")}
										value={ticketIdInput}
										onChange={(e) => setTicketIdInput(e.target.value)}
									/>
								</FilterField>
							) : null}

							{scope === "contact" ? (
								<div className="space-y-2">
									<FilterField label={i18n.t("adminAiReports.fields.contact")}>
										<input
											type="search"
											className="form-input w-full"
											placeholder={i18n.t("adminAiReports.contactPlaceholder")}
											value={
												selectedContact
													? `${selectedContact.name}${
															selectedContact.number
																? ` · ${selectedContact.number}`
																: ""
													  }`
													: contactQuery
											}
											onChange={(e) => {
												setSelectedContact(null);
												setContactQuery(e.target.value);
											}}
										/>
									</FilterField>
									{selectedContact ? (
										<button
											type="button"
											className="text-xs font-semibold text-primary hover:underline"
											onClick={() => {
												setSelectedContact(null);
												setContactQuery("");
											}}
										>
											{i18n.t("adminAiReports.clearContact")}
										</button>
									) : null}
									{!selectedContact && contactQuery.trim().length >= 2 ? (
										<div className="max-h-48 overflow-y-auto rounded-xl border border-default-200 dark:border-default-700">
											{searchingContacts ? (
												<p className="m-0 px-3 py-3 text-sm text-default-500">
													{i18n.t("adminAiReports.searchingContacts")}
												</p>
											) : contactResults.length ? (
												contactResults.map((contact) => (
													<button
														key={contact.id}
														type="button"
														className="flex w-full flex-col items-start gap-0.5 border-b border-default-100 px-3 py-2 text-left last:border-b-0 hover:bg-default-50 dark:border-default-800 dark:hover:bg-default-800"
														onClick={() => {
															setSelectedContact(contact);
															setContactQuery("");
															setContactResults([]);
														}}
													>
														<span className="text-sm font-medium text-default-900 dark:text-default-50">
															{contact.name}
														</span>
														<span className="text-xs text-default-500">
															{contact.number || `#${contact.id}`}
														</span>
													</button>
												))
											) : (
												<p className="m-0 px-3 py-3 text-sm text-default-500">
													{i18n.t("adminAiReports.emptyContacts")}
												</p>
											)}
										</div>
									) : null}
								</div>
							) : null}

							{scope === "team" ? (
								<>
									<div>
										<p className="mb-2 text-sm font-medium text-default-700 dark:text-default-200">
											{i18n.t("adminAiReports.fields.periodType")}
										</p>
										<div className="flex flex-wrap gap-2">
											{PERIOD_TYPES.map((opt) => {
												const active = periodType === opt.value;
												return (
													<button
														key={opt.value}
														type="button"
														onClick={() => setPeriodType(opt.value)}
														className={clsx(
															"rounded-full px-3.5 py-1.5 text-xs font-semibold transition",
															active
																? "bg-primary text-white"
																: "border border-default-200 text-default-600 hover:border-primary/20 hover:bg-primary/10 hover:text-primary dark:border-default-700 dark:text-default-300"
														)}
													>
														{i18n.t(opt.labelKey)}
													</button>
												);
											})}
										</div>
									</div>

									<div
										className={clsx(
											"grid gap-4",
											needsRange
												? "md:grid-cols-2 xl:grid-cols-4"
												: "md:grid-cols-2 xl:grid-cols-3"
										)}
									>
										<FilterField
											label={
												needsRange
													? i18n.t("adminAiReports.fields.dateFrom")
													: i18n.t("adminAiReports.fields.date")
											}
										>
											<input
												type={needsRange ? "datetime-local" : "date"}
												className="form-input w-full"
												value={dateFrom}
												onChange={(e) => setDateFrom(e.target.value)}
											/>
										</FilterField>

										{needsRange ? (
											<FilterField label={i18n.t("adminAiReports.fields.dateTo")}>
												<input
													type="datetime-local"
													className="form-input w-full"
													value={dateTo}
													onChange={(e) => setDateTo(e.target.value)}
												/>
											</FilterField>
										) : null}

										<MultiFilterSelect
											label={i18n.t("adminAiReports.fields.queues")}
											options={queues}
											selectedIds={queueIds}
											onChange={setQueueIds}
											placeholder={i18n.t("adminAiReports.allQueues")}
											emptyText={i18n.t("adminAiReports.emptyQueues")}
										/>

										<MultiFilterSelect
											label={i18n.t("adminAiReports.fields.users")}
											options={users}
											selectedIds={userIds}
											onChange={setUserIds}
											placeholder={i18n.t("adminAiReports.allUsers")}
											emptyText={i18n.t("adminAiReports.emptyUsers")}
										/>
									</div>
								</>
							) : null}

							<div>
								<p className="mb-2 text-sm font-medium text-default-700 dark:text-default-200">
									{i18n.t("adminAiReports.fields.focuses")}
								</p>
								<div className="flex flex-wrap gap-2">
									{FOCUS_KEYS.map((key) => {
										const active = focuses.includes(key);
										return (
											<button
												key={key}
												type="button"
												onClick={() => toggleFocus(key)}
												className={clsx(
													"rounded-full border px-3.5 py-1.5 text-xs font-semibold transition",
													active
														? "border-primary/30 bg-primary/10 text-primary"
														: "border-default-200 text-default-600 hover:border-primary/20 hover:bg-primary/5 dark:border-default-700 dark:text-default-300"
												)}
											>
												{i18n.t(`adminAiReports.focuses.${key}`)}
											</button>
										);
									})}
								</div>
							</div>

							<div className="rounded-xl border border-default-200 bg-default-50/80 px-4 py-3 dark:border-default-700 dark:bg-default-950/40">
								<label className="flex cursor-pointer items-start gap-3">
									<input
										type="checkbox"
										className="mt-1"
										checked={focuses.includes(MESSAGE_FOCUS)}
										onChange={() => toggleFocus(MESSAGE_FOCUS)}
									/>
									<span>
										<span className="block text-sm font-semibold text-default-800 dark:text-default-100">
											{i18n.t("adminAiReports.focuses.messages")}
										</span>
										<span className="mt-0.5 block text-xs text-default-500 dark:text-default-400">
											{i18n.t("adminAiReports.messagesHint")}
										</span>
									</span>
								</label>
								{readsMessages && scope === "team" && !focuses.includes(MESSAGE_FOCUS) ? (
									<p className="m-0 mt-2 text-xs text-primary">
										{i18n.t("adminAiReports.messagesAutoHint")}
									</p>
								) : null}
								{scope !== "team" ? (
									<p className="m-0 mt-2 text-xs text-primary">
										{i18n.t("adminAiReports.messagesScopeHint")}
									</p>
								) : null}
							</div>

							<FilterField label={i18n.t("adminAiReports.fields.question")}>
								<textarea
									rows={3}
									className="form-input w-full resize-y"
									placeholder={i18n.t("adminAiReports.questionPlaceholder")}
									value={question}
									onChange={(e) => setQuestion(e.target.value)}
								/>
							</FilterField>

							<div className="flex flex-wrap items-center justify-between gap-3 border-t border-default-200 pt-4 dark:border-default-700">
								<button
									type="button"
									className="btn btn-sm btn-sub-gray"
									onClick={handleClearFilters}
									disabled={generating}
								>
									{i18n.t("adminAiReports.clear")}
								</button>
								<button
									type="button"
									onClick={handleGenerate}
									disabled={generating}
									className="btn btn-sm inline-flex items-center gap-2 bg-primary text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
								>
									{generating ? (
										<>
											<CircularProgress size={14} color="inherit" />
											{i18n.t("adminAiReports.generating")}
										</>
									) : (
										<>
											<IconSpark />
											{i18n.t("adminAiReports.generate")}
										</>
									)}
								</button>
							</div>
						</div>
					</div>

					<div className="grid gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
						<div className="card h-fit">
							<div className="card-header flex items-center justify-between gap-2">
								<div>
									<h6 className="card-title m-0">
										{i18n.t("adminAiReports.historyTitle")}
									</h6>
									<p className="m-0 mt-0.5 text-xs text-default-500">
										{i18n.t("adminAiReports.historyCount", {
											count: reports.length,
										})}
									</p>
								</div>
								<button
									type="button"
									className="btn btn-icon size-8 shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/20 hover:bg-primary/10 hover:text-primary dark:border-default-700"
									onClick={loadReports}
									disabled={loadingList}
									title={i18n.t("adminAiReports.refresh")}
								>
									{loadingList ? (
										<CircularProgress size={14} />
									) : (
										<IconRefresh />
									)}
								</button>
							</div>

							<div className="card-body space-y-2 pt-0">
								{loadingList && !reports.length ? (
									<div className="flex justify-center py-8">
										<CircularProgress size={24} />
									</div>
								) : null}

								{!loadingList && !reports.length ? (
									<div className="rounded-xl border border-dashed border-default-300 px-3 py-8 text-center text-sm text-default-500 dark:border-default-700">
										{i18n.t("adminAiReports.emptyHistory")}
									</div>
								) : null}

								{reports.map((report) => {
									const active = selectedId === report.id;
									const whenDay = formatMessageCalendarDay(
										report.createdAt,
										timeZone
									);
									const whenClock = formatMessageClock(
										report.createdAt,
										timeZone
									);
									return (
										<button
											key={report.id}
											type="button"
											onClick={() => setSelectedId(report.id)}
											className={clsx(
												"w-full rounded-xl border px-3 py-3 text-left transition",
												active
													? "border-primary/40 bg-primary/5 shadow-sm"
													: "border-default-200 bg-transparent hover:border-default-300 hover:bg-default-50 dark:border-default-700 dark:hover:bg-default-800/60"
											)}
										>
											<p className="m-0 line-clamp-2 text-sm font-semibold text-default-900 dark:text-default-50">
												{report.title}
											</p>
											<p className="m-0 mt-1 text-xs text-default-500">
												{whenDay} · {whenClock}
											</p>
											{report.createdByName ? (
												<p className="m-0 mt-0.5 truncate text-xs text-default-400">
													{report.createdByName}
												</p>
											) : null}
										</button>
									);
								})}
							</div>
						</div>

						<div className="min-w-0">
							{selectedReport ? (
								<ReportDetail report={selectedReport} timeZone={timeZone} />
							) : (
								<div className="card">
									<div className="card-body flex min-h-[280px] items-center justify-center px-6 py-10 text-center">
										<div>
											<div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
												<IconSpark />
											</div>
											<p className="m-0 text-sm font-medium text-default-700 dark:text-default-200">
												{i18n.t("adminAiReports.emptySelection")}
											</p>
											<p className="m-0 mt-1 text-sm text-default-500">
												{i18n.t("adminAiReports.emptySelectionHint")}
											</p>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
